import React, { useState } from 'react';
import axios from 'axios';
import { ExternalLink, Tag, Trash2, Edit } from 'lucide-react';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import config from '../config';

const BACKEND_URL = config.BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ToolCard = ({ tool, showActions = false, onDelete }) => {
  const [deleting, setDeleting] = useState(false);
  const { isAdmin, getAuthHeader } = useAuth();

  const handleDelete = async () => {
    console.log('Delete button clicked for tool:', tool.name, tool.id);
    
    // Skip confirmation dialog as it's blocked by sandbox
    // User will see immediate feedback via toast notifications
    
    console.log('Proceeding with delete...');
    setDeleting(true);
    try {
      const headers = getAuthHeader();
      console.log('Auth headers:', headers);
      
      const response = await axios.delete(`${API}/tools/${tool.id}`, {
        headers: headers
      });
      
      console.log('Delete response:', response);
      toast.success(`${tool.name} deleted successfully`);
      if (onDelete) {
        onDelete();
      }
    } catch (error) {
      console.error('Error deleting tool:', error);
      console.error('Error response:', error.response);
      toast.error(error.response?.data?.detail || 'Failed to delete tool');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="group relative bg-gray-800/40 backdrop-blur-sm rounded-2xl border border-gray-700/50 hover:border-blue-500/50 transition-all hover:shadow-lg hover:shadow-blue-500/10 overflow-hidden">
      {/* Icon Header */}
      <div className="p-6 pb-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-4 flex-1 min-w-0">
            {/* Tool Icon */}
            <div className="w-12 h-12 rounded-xl bg-gray-700/50 flex items-center justify-center flex-shrink-0 overflow-hidden">
              {tool.icon_url ? (
                <img
                  src={tool.icon_url}
                  alt={tool.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.parentElement.innerHTML = `<div class="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-bold text-sm">${tool.name.charAt(0).toUpperCase()}</div>`;
                  }}
                />
              ) : (
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-bold text-sm">
                  {tool.name.charAt(0).toUpperCase()}
                </div>
              )}
            </div>

            {/* Tool Info */}
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-semibold text-white mb-1 truncate" title={tool.name}>
                {tool.name}
              </h3>
              <p className="text-sm text-gray-400 line-clamp-2">
                {tool.description}
              </p>
            </div>
          </div>

          {/* Actions */}
          {showActions && isAdmin() && (
            <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                type="button"
                data-testid={`delete-tool-${tool.id}`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log('Delete button clicked!', tool.name);
                  handleDelete();
                }}
                disabled={deleting}
                className="p-2 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed z-10 relative"
                title={deleting ? "Deleting..." : "Delete tool"}
              >
                {deleting ? (
                  <div className="w-4 h-4 border-2 border-red-400 border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4" />
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Hierarchical Taxonomy Display */}
      <div className="px-6 pb-4">
        {/* Display hierarchical categories with subtags */}
        {tool.categories && tool.categories.length > 0 ? (
          <div className="space-y-3">
            {tool.categories.slice(0, 2).map((categoryObj, idx) => (
              <div key={idx} className="space-y-1.5">
                {/* Category Badge */}
                <div className="flex items-center gap-2">
                  <Badge className="bg-gradient-to-r from-blue-500/20 to-purple-500/20 text-blue-300 border-blue-500/30 font-medium">
                    {categoryObj.category}
                  </Badge>
                </div>
                
                {/* Subtags under this category */}
                {categoryObj.subtags && categoryObj.subtags.length > 0 && (
                  <div className="flex items-center gap-1.5 flex-wrap ml-2">
                    <Tag className="w-3 h-3 text-gray-500 flex-shrink-0" />
                    {categoryObj.subtags.slice(0, 4).map((subtag, subIdx) => (
                      <Badge
                        key={subIdx}
                        variant="secondary"
                        className="bg-gray-700/30 text-gray-400 hover:bg-gray-700/50 text-xs border-gray-600/30"
                      >
                        {subtag}
                      </Badge>
                    ))}
                    {categoryObj.subtags.length > 4 && (
                      <span className="text-xs text-gray-500">+{categoryObj.subtags.length - 4}</span>
                    )}
                  </div>
                )}
              </div>
            ))}
            
            {/* Show if there are more categories */}
            {tool.categories.length > 2 && (
              <div className="text-xs text-gray-500 ml-2">
                +{tool.categories.length - 2} more {tool.categories.length - 2 === 1 ? 'category' : 'categories'}
              </div>
            )}
          </div>
        ) : (
          /* Fallback to old structure for tools not yet migrated */
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Badge className="bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 border-blue-500/20">
                {tool.category || 'Other'}
              </Badge>
            </div>
            
            {tool.tags && tool.tags.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap">
                <Tag className="w-3 h-3 text-gray-500" />
                {tool.tags.slice(0, 3).map((tag, index) => (
                  <Badge
                    key={index}
                    variant="secondary"
                    className="bg-gray-700/30 text-gray-400 hover:bg-gray-700/50 text-xs"
                  >
                    {tag}
                  </Badge>
                ))}
                {tool.tags.length > 3 && (
                  <span className="text-xs text-gray-500">+{tool.tags.length - 3} more</span>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Visit Link */}
      <div className="px-6 pb-6">
        <a
          href={tool.url}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`visit-tool-${tool.id}`}
          className="inline-flex items-center gap-2 w-full justify-center px-4 py-2 rounded-lg bg-gray-700/50 hover:bg-blue-500/20 text-gray-300 hover:text-blue-400 border border-gray-600/50 hover:border-blue-500/50 font-medium transition-all"
        >
          <ExternalLink className="w-4 h-4" />
          Visit Website
        </a>
      </div>
    </div>
  );
};

export default ToolCard;